import { useEffect, useRef, useState } from 'react';

/**
 * Smoothly interpolates a numeric value towards `target` using
 * requestAnimationFrame (easeOutCubic). When the target changes mid-flight,
 * the animation resumes from the current displayed value.
 *
 * First render shows the target as-is (no animation on mount).
 */
export function useCountUp(target: number, duration = 400): number {
  const [display, setDisplay] = useState(target);
  const currentRef = useRef(target);

  useEffect(() => {
    const from = currentRef.current;
    if (from === target) return;

    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const value = from + (target - from) * eased;
      currentRef.current = value;
      setDisplay(value);
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return display;
}

export default useCountUp;
